import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { API, api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";

/** Owner-OTP gated export of the customer / lead list. */
export function SecureExportDialog({ entity, open, onOpenChange }) {
  const [step, setStep] = useState("intro");
  const [code, setCode] = useState("");
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const label = entity === "customers" ? "customer list" : "lead list";

  const requestOtp = async (fmt) => {
    setBusy(true);
    try {
      const { data } = await api.post("/secure-export/request-otp", { entity, fmt });
      setInfo({ ...data, fmt });
      setStep("otp");
      toast.success(`Code sent to the Owner (${data.owner_mobile})`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    if (code.trim().length < 4) return toast.error("Enter the 6-digit code");
    window.open(
      `${API}/secure-export/download?entity=${entity}&fmt=${info.fmt}&code=${encodeURIComponent(code.trim())}`,
      "_blank");
    toast.success("If the code is correct the file will download");
    setTimeout(() => { onOpenChange(false); setStep("intro"); setCode(""); }, 1200);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) { setStep("intro"); setCode(""); } }}>
      <DialogContent data-testid="secure-export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-1.5">
            <ShieldCheck className="h-4 w-4" /> Export the {label}
          </DialogTitle>
          <DialogDescription>
            This list is protected company property. A one-time code goes to the Owner's verified
            number before any file is created — it works once and expires in 3 minutes.
          </DialogDescription>
        </DialogHeader>

        {step === "intro" ? (
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => requestOtp("csv")} disabled={busy}
              data-testid="export-request-csv">
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Excel / CSV
            </Button>
            <Button variant="outline" onClick={() => requestOtp("pdf")} disabled={busy}
              data-testid="export-request-pdf">
              PDF
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Badge variant="outline" data-testid="otp-target-badge">
              Code sent to {info?.owner_mobile} • {info?.channels?.join(", ")}
            </Badge>
            <Input placeholder="6-digit code from the Owner" value={code} maxLength={6}
              data-testid="export-otp-input" onChange={(e) => setCode(e.target.value)} />
            <p className="text-[11px] text-muted-foreground">
              Ask the Owner for the code. Every attempt — successful or not — is logged.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          {step === "otp" && (
            <Button onClick={download} data-testid="export-download-button">
              Verify &amp; download
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Camera + file capture used on Leads, Purchases and Expenses. */
export function CameraUpload({ onFiles, accept = "image/*,application/pdf", multiple = true,
                              label = "Add bill", testId = "camera-upload" }) {
  const camera = useState(null)[0];
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-accent">
        <input type="file" accept="image/*" capture="environment" hidden multiple={multiple}
          data-testid={`${testId}-camera`} onChange={(e) => onFiles(e.target.files)} />
        Snap with camera
      </label>
      <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-border px-2.5 py-1.5 text-xs hover:bg-accent">
        <input type="file" accept={accept} hidden multiple={multiple} ref={camera}
          data-testid={`${testId}-file`} onChange={(e) => onFiles(e.target.files)} />
        {label}
      </label>
    </div>
  );
}
